import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import { allSpecials, getSpecialById, getWorkById } from "@/utils/media";
import { usePageSeo } from "@/hooks/usePageSeo";

export default function SpecialPage() {
  const { id } = useParams<{ id: string }>();
  const special = getSpecialById(id);

  usePageSeo({
    title: special ? `${special.title} - 91av视频专题合集` : "专题合集 - 91av视频",
    description: special ? `${special.intro} 收录 ${special.workIds.length} 部影视作品。` : "91av视频专题合集页。",
    keywords: special ? `91av视频,${special.title},专题合集,影视资源` : "91av视频,专题合集",
  });

  if (!special) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-20 text-center md:px-8">
        <i className="ri-bookmark-3-line text-5xl text-foreground-400" />
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">专题不存在</h1>
        <p className="mt-2 text-sm text-foreground-600">该专题不存在或已下线。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const relatedSpecials = allSpecials.filter((s) => s.id !== special.id);

  return (
    <div className="w-full">
      <div className="relative h-[340px] w-full overflow-hidden md:h-[420px]">
        <img
          src={special.cover}
          alt={`${special.title} 专题主图`}
          title={`${special.title} - 91av视频专题合集`}
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/60 to-foreground-950/30" />
        <div className="absolute inset-0">
          <div className="mx-auto flex h-full w-full max-w-[1280px] flex-col justify-end px-4 pb-8 md:px-8">
            <Breadcrumb
              items={[{ label: "首页", to: "/" }, { label: "专题合集" }, { label: special.title }]}
            />
            <span className="mt-3 inline-flex w-fit items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
              <i className="ri-bookmark-3-line" />
              专题合集 · {special.workIds.length} 部作品
            </span>
            <h1 className="mt-3 font-heading text-3xl font-bold text-background-50 md:text-5xl">
              {special.title}
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-background-100/85 md:text-base">
              {special.subtitle}
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:col-span-2">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-quill-pen-line text-primary-700" />
              专题导语
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{special.intro}</p>
          </div>
          <div className="rounded-lg border border-secondary-200 bg-secondary-100/70 p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-secondary-900">
              <i className="ri-lightbulb-line" />
              推荐理由
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-secondary-900/90">{special.reason}</p>
          </div>
        </div>

        {special.groups.map((group) => {
          const groupWorks = group.workIds
            .map((wid) => getWorkById(wid))
            .filter((w): w is NonNullable<typeof w> => Boolean(w));
          return (
            <section key={group.name} className="mt-10">
              <div className="mb-4 flex items-center gap-3">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                <h2 className="font-heading text-lg font-bold text-foreground-950">{group.name}</h2>
                <span className="text-xs text-foreground-500">{groupWorks.length} 部</span>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
                {groupWorks.map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>
            </section>
          );
        })}

        <section className="mt-12">
          <div className="mb-5 flex items-center gap-2">
            <i className="ri-links-line text-xl text-primary-700" />
            <h2 className="font-heading text-lg font-bold text-foreground-950">相关专题推荐</h2>
          </div>
          <div className="no-scrollbar -mx-1 flex gap-4 overflow-x-auto px-1 pb-2">
            {relatedSpecials.map((sp) => (
              <Link
                key={sp.id}
                to={`/special/${sp.id}`}
                className="group flex w-[280px] shrink-0 flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-transform duration-300 hover:-translate-y-1 md:w-[320px]"
              >
                <div className="relative aspect-[16/9] w-full overflow-hidden bg-background-200">
                  <img
                    src={sp.cover}
                    alt={`${sp.title} 专题封面`}
                    title={`${sp.title} - 91av视频专题`}
                    className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700">
                    {sp.title}
                  </h3>
                  <p className="mt-1.5 line-clamp-2 text-xs text-foreground-600">{sp.subtitle}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}